import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { Navbar } from '../../../components';
import { getLivestreamById } from '../../../api/livestream';
import styles from './index.module.css';
import { useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

type LayoutType = 'single' | 'grid2' | 'grid4';

function LivestreamPlayer() {
  const { id } = useParams<{ id: string }>();
  const [selectedCameras, setSelectedCameras] = useState<number[]>([0]);
  const [layout, setLayout] = useState<LayoutType>('single');

  const { data: livestream, isLoading, error } = useQuery({
    queryKey: ['getLivestreamById', id],
    queryFn: () => getLivestreamById(id!),
    enabled: !!id,
    refetchInterval: 60000, // Refresh stream URLs a cada minuto
  });

  const handleCameraToggle = (cameraIndex: number) => {
    if (layout === 'single') {
      setSelectedCameras([cameraIndex]);
    } else {
      const maxCameras = layout === 'grid2' ? 2 : 4;
      if (selectedCameras.includes(cameraIndex)) {
        if (selectedCameras.length > 1) {
          setSelectedCameras(selectedCameras.filter(c => c !== cameraIndex));
        }
      } else {
        if (selectedCameras.length < maxCameras) {
          setSelectedCameras([...selectedCameras, cameraIndex]);
        } else {
          // Substitui a primeira câmera
          setSelectedCameras([...selectedCameras.slice(1), cameraIndex]);
        }
      }
    }
  };

  const handleLayoutChange = (newLayout: LayoutType) => {
    setLayout(newLayout);
    if (newLayout === 'single') {
      setSelectedCameras([selectedCameras[0]]);
    }
  };

  const getGridClass = () => {
    switch (layout) {
      case 'grid2':
        return styles.player_grid_2;
      case 'grid4':
        return styles.player_grid_4;
      default:
        return styles.player_single;
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar menuTextColor="white" />
        <div className={styles.loading}>
          <p>Carregando livestream...</p>
        </div>
      </>
    );
  }

  if (error || !livestream) {
    return (
      <>
        <Navbar menuTextColor="white" />
        <div className={styles.error}>
          <h2>Livestream não encontrada</h2>
          <p>A transmissão pode ter terminado ou não está disponível.</p>
          <Link to="/livestream" className={styles.back_link}>
            <ArrowBackIcon /> Voltar para Livestreams
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar menuTextColor="white" />
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/livestream" className={styles.back_button}>
            <ArrowBackIcon />
            <span>Voltar</span>
          </Link>

          <div className={styles.stream_info}>
            <div className={styles.title_row}>
              {livestream.isLive && (
                <span className={styles.live_badge}>
                  <span className={styles.live_dot}></span>
                  AO VIVO
                </span>
              )}
              <h1 className={styles.title}>{livestream.arenaName}</h1>
            </div>
            <p className={styles.court}>{livestream.courtName}</p>
            <div className={styles.meta}>
              <span className={styles.meta_item}>
                <LocationOnIcon className={styles.meta_icon} />
                {livestream.city}, {livestream.state}
              </span>
              {livestream.viewerCount !== undefined && (
                <span className={styles.viewers}>
                  {livestream.viewerCount} assistindo
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Layout Controls */}
        {livestream.cameras.length > 1 && (
          <div className={styles.layout_controls}>
            <span className={styles.layout_label}>Layout:</span>
            <button
              className={`${styles.layout_btn} ${layout === 'single' ? styles.layout_btn_active : ''}`}
              onClick={() => handleLayoutChange('single')}
              title="Uma câmera"
            >
              <FullscreenIcon />
            </button>
            {livestream.cameras.length >= 2 && (
              <button
                className={`${styles.layout_btn} ${layout === 'grid2' ? styles.layout_btn_active : ''}`}
                onClick={() => handleLayoutChange('grid2')}
                title="Duas câmeras"
              >
                <GridViewIcon />
              </button>
            )}
            {livestream.cameras.length >= 4 && (
              <button
                className={`${styles.layout_btn} ${layout === 'grid4' ? styles.layout_btn_active : ''}`}
                onClick={() => handleLayoutChange('grid4')}
                title="Quatro câmeras"
              >
                <ViewModuleIcon />
              </button>
            )}
          </div>
        )}

        {/* Camera Selector */}
        <div className={styles.camera_selector}>
          <span className={styles.camera_label}>
            <VideocamIcon className={styles.camera_label_icon} />
            Câmeras:
          </span>
          <div className={styles.camera_buttons}>
            {livestream.cameras.map((camera: any, index: number) => (
              <button
                key={camera.id}
                className={`${styles.camera_btn} ${selectedCameras.includes(index) ? styles.camera_btn_active : ''}`}
                onClick={() => handleCameraToggle(index)}
              >
                <VideocamIcon className={styles.camera_btn_icon} />
                {camera.name || `Câmera ${index + 1}`}
              </button>
            ))}
          </div>
        </div>

        {/* Player Grid */}
        <div className={`${styles.player_container} ${getGridClass()}`}>
          {selectedCameras.map((cameraIndex) => {
            const camera = livestream.cameras[cameraIndex];
            if (!camera) return null;

            return (
              <div key={camera.id} className={styles.player_wrapper}>
                <div className={styles.player_header}>
                  <VideocamIcon className={styles.player_header_icon} />
                  <span>{camera.name || `Câmera ${cameraIndex + 1}`}</span>
                </div>
                <div className={styles.player}>
                  <ReactPlayer
                    url={camera.streamUrl}
                    playing
                    controls
                    width="100%"
                    height="100%"
                    config={{
                      file: {
                        forceHLS: true,
                        hlsOptions: {
                          enableWorker: true,
                          lowLatencyMode: true,
                        },
                      },
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default LivestreamPlayer;
